/**
 * RoleSheetsPage — Bảng phân vai (Role Assignment Sheet) — Vòng Bán kết / Chung kết.
 *
 * Audit 2026-05-21 (F.2): tách khỏi MemorialsPage. Chỉ visible cho đội đã vượt
 * Vòng Sơ loại (`team.competition_status === 'advanced_to_semifinal'`).
 * Admin (ASSIGN_CASES) luôn thấy mọi case + mọi đội để review.
 */
import { useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { Assignment, CloudUpload, CloudDownload, Delete, HourglassEmpty } from '@mui/icons-material'

import { useAppSelector } from '../hooks/redux'
import { useGetTeamsQuery } from '../store/api/teamsApi'
import { useGetCasesQuery } from '../store/api/casesApi'
import {
  useListRoleSheetsQuery,
  useUploadRoleSheetMutation,
  useDeleteRoleSheetMutation,
  useLazyDownloadRoleSheetQuery,
} from '../store/api/bracketsApi'
import { hasPermission } from '../utils/permissions'
import { PageHeader } from '../components/PageHeader'
import { gradientBrand } from '../theme/brand'
import { getFilterPaperSx } from '../theme/sectionSurfaces'

export default function RoleSheetsPage() {
  const { user: currentUser } = useAppSelector((state) => state.auth)
  const { data: teams = [] } = useGetTeamsQuery()
  const { data: cases = [] } = useGetCasesQuery()

  const canAssignCases = currentUser && hasPermission(currentUser.role, 'ASSIGN_CASES')
  const canSubmitMemorial = currentUser && hasPermission(currentUser.role, 'SUBMIT_MEMORIAL')

  const userTeam = useMemo(
    () => teams.find((t) => t.members.includes(currentUser?.id || '')),
    [teams, currentUser?.id],
  )

  // F.2 — gate cho student: chỉ đội đã advance qua Vòng Sơ loại mới thấy.
  const studentTeamAdvanced =
    !!userTeam && (userTeam as any).competition_status === 'advanced_to_semifinal'

  const accessibleCases = useMemo(() => {
    if (canAssignCases) return cases
    if (userTeam) return cases.filter((c) => (c.assigned_team_ids || []).includes(userTeam.id))
    return []
  }, [cases, canAssignCases, userTeam])

  const [selectedCaseId, setSelectedCaseId] = useState<string>('')
  // Auto-select first case
  useMemo(() => {
    if (!selectedCaseId && accessibleCases.length > 0) {
      setSelectedCaseId(accessibleCases[0].id)
    }
  }, [selectedCaseId, accessibleCases])

  const {
    data: roleSheets = [],
    refetch: refetchRoleSheets,
  } = useListRoleSheetsQuery(selectedCaseId, { skip: !selectedCaseId })
  const [uploadRoleSheet, { isLoading: isUploading }] = useUploadRoleSheetMutation()
  const [deleteRoleSheet] = useDeleteRoleSheetMutation()
  const [downloadRoleSheet] = useLazyDownloadRoleSheetQuery()

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false,
    message: '',
    severity: 'success',
  })

  // ─── Gate hiển thị ──────────────────────────────────────────────────────
  // Student chưa pass R1 → hiển thị state chờ, KHÔNG mở UI nộp.
  const canViewAccess = canAssignCases || studentTeamAdvanced

  const handleUpload = async (file?: File) => {
    if (!file || !selectedCaseId) return
    if (file.type !== 'application/pdf') {
      setSnackbar({ open: true, message: 'Bảng phân vai chỉ chấp nhận PDF.', severity: 'error' })
      return
    }
    try {
      await uploadRoleSheet({ caseId: selectedCaseId, file }).unwrap()
      setSnackbar({ open: true, message: `Đã nộp Bảng phân vai: ${file.name}`, severity: 'success' })
      refetchRoleSheets()
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: `Nộp thất bại: ${err?.data?.detail || err?.message || 'Lỗi không xác định'}`,
        severity: 'error',
      })
    }
  }

  const handleDownload = async (id: string, filename?: string) => {
    try {
      const blob = await downloadRoleSheet(id).unwrap()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename || 'bang-phan-vai.pdf'
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: `Tải thất bại: ${err?.data?.detail || err?.message || ''}`,
        severity: 'error',
      })
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Xoá Bảng phân vai này?')) return
    try {
      await deleteRoleSheet(id).unwrap()
      setSnackbar({ open: true, message: 'Đã xoá Bảng phân vai.', severity: 'success' })
      refetchRoleSheets()
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: `Xoá thất bại: ${err?.data?.detail || err?.message || ''}`,
        severity: 'error',
      })
    }
  }

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 4 }, px: { xs: 2, sm: 3 } }}>
      <PageHeader
        title="Bảng phân vai"
        subtitle="File PDF phân công vai trò luật sư / nguyên đơn / bị đơn — áp dụng cho Vòng Bán kết và Chung kết."
        icon={<Assignment />}
      />

      {/* F.2 — Gate: student chưa pass R1 */}
      {!canViewAccess && (
        <Alert severity="info" icon={<HourglassEmpty />} sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Bảng phân vai chưa được mở
          </Typography>
          <Typography variant="body2">
            Tính năng này chỉ áp dụng cho các đội đã vượt qua Vòng Sơ loại
            (vào Vòng Bán kết). Hiện đội{' '}
            {userTeam ? <strong>{userTeam.name}</strong> : 'của bạn'} chưa đủ điều kiện —
            vui lòng theo dõi mục Thông báo để biết kết quả công bố.
          </Typography>
        </Alert>
      )}

      {canViewAccess && (
        <>
          {/* Case selector */}
          <Paper elevation={0} sx={(t) => ({ ...getFilterPaperSx(t), mb: 2 })}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
              <FormControl fullWidth sx={{ maxWidth: { sm: 480 } }} size="small">
                <InputLabel>Cuộc thi</InputLabel>
                <Select
                  value={selectedCaseId}
                  label="Cuộc thi"
                  onChange={(e) => setSelectedCaseId(e.target.value)}
                >
                  {accessibleCases.length === 0 ? (
                    <MenuItem value="" disabled>
                      — Không có cuộc thi —
                    </MenuItem>
                  ) : (
                    accessibleCases.map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.title || '— Chưa đặt tên —'}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>

              {canSubmitMemorial && userTeam && studentTeamAdvanced && (
                <Button
                  component="label"
                  variant="contained"
                  startIcon={<CloudUpload />}
                  disabled={isUploading || !selectedCaseId}
                  sx={{ background: gradientBrand, textTransform: 'none' }}
                >
                  {isUploading ? 'Đang nộp...' : 'Nộp Bảng phân vai'}
                  <input
                    hidden
                    type="file"
                    accept=".pdf"
                    onChange={(e) => handleUpload(e.target.files?.[0])}
                  />
                </Button>
              )}
            </Stack>
          </Paper>

          {!selectedCaseId ? (
            <Alert severity="info">Chọn cuộc thi để xem Bảng phân vai.</Alert>
          ) : roleSheets.length === 0 ? (
            <Alert severity="info">
              Chưa có Bảng phân vai cho cuộc thi này.
              {canSubmitMemorial && userTeam && studentTeamAdvanced && ' Đội bạn có thể nộp bằng nút bên trên.'}
            </Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 700 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ minWidth: 220 }}><strong>Tên file</strong></TableCell>
                    <TableCell sx={{ minWidth: 160 }}><strong>Đội nộp</strong></TableCell>
                    <TableCell sx={{ minWidth: 100 }}><strong>Vai trò</strong></TableCell>
                    <TableCell sx={{ minWidth: 100 }}><strong>Kích thước</strong></TableCell>
                    <TableCell sx={{ minWidth: 140 }}><strong>Ngày nộp</strong></TableCell>
                    <TableCell sx={{ minWidth: 130 }}><strong>Trạng thái</strong></TableCell>
                    <TableCell align="right" sx={{ minWidth: 130 }}><strong>Thao tác</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {roleSheets.map((s) => {
                    const team = teams.find((t) => t.id === s.team_id)
                    const isOwn = userTeam && s.team_id === userTeam.id
                    return (
                      <TableRow key={s.id} hover>
                        <TableCell>{s.title || s.file_name}</TableCell>
                        <TableCell>
                          {team?.name || (s.team_id ? '— Đội chưa rõ —' : '— BTC —')}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={s.uploader_role || (s.team_id ? 'team' : 'btc')}
                            color={s.team_id ? 'primary' : 'default'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          {s.file_size != null ? `${(s.file_size / 1024).toFixed(1)} KB` : '—'}
                        </TableCell>
                        <TableCell>
                          {s.uploaded_at ? new Date(s.uploaded_at).toLocaleString('vi-VN') : '—'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={s.team_id ? 'Đã nộp' : 'BTC ban hành'}
                            color={s.team_id ? 'success' : 'info'}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={() => s.id && handleDownload(s.id, s.file_name ?? undefined)}
                            title="Tải xuống"
                          >
                            <CloudDownload fontSize="small" />
                          </IconButton>
                          {isOwn && s.id && (
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(s.id!)}
                              color="error"
                              title="Xoá"
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  )
}
